import React from 'react'
import { Link } from 'react-router-dom'

import EuroIcon from '../../../assets/icons/currency-euro.svg'
import BriefcaseIcon from '../../../assets/icons/brief-case.svg'
import CalendarIcon from '../../../assets/icons/calendar.svg'
import Tag from '../content/Tag'
import { useI18N } from '../../../libraries/i18n/hook'
import * as dateUtils from '../../../libraries/date'
import Translate from '../../../libraries/i18n/Translate'
import CheckedIcon from '../../../assets/icons/checked.svg'
import BloomIcon from '../../../assets/bloom-favicon.svg'
import InfoIcon from '../../../assets/icons/info.svg?react'
import SparksIcon from '../../../assets/icons/sparks.svg'

type Props = {
  isOpened: boolean
  title: string
  company: { logoSrc: string, name: string }
  city?: string
  workTypes?: string
  workShift?: string
  salary?: string
  experience?: string
  startDateInfo?: string
  isCurricular?: boolean
  isInternship: boolean
  isRecruitingEvent: boolean
  lastPublishedAt: Date
  applied?: boolean
  engaged?: boolean
  isInternal?: boolean
}
const JobOfferCard: React.FC<Props> = ({
  isOpened, title, company, city, workTypes, workShift, salary, experience, startDateInfo, isCurricular, isInternship, isRecruitingEvent, lastPublishedAt, applied, engaged, isInternal
}) => {
  const { t } = useI18N()
  const renderDaysAgo = () => {
    return (
      <div className='font-light text-xs'>
        <Translate i18nKey='common.daysAgo' values={{ context: dateUtils.getDaysSinceDate(lastPublishedAt), days: dateUtils.getDaysSinceDate(lastPublishedAt) }} />
      </div>
    )
  }

  const renderInternshipTag = () => (
    isInternship && <span className='ml-4 hidden sm:inline-block'><Tag solid variant='electric-green'>{t('jobs.internship')}</Tag></span>
  )

  const renderRecruitingEventTag = () => (
    isRecruitingEvent && <span className='ml-4 hidden sm:inline-block'><Tag solid variant='deep-green'>{t('jobs.recruiting.event')}</Tag></span>
  )

  const renderOfferStatus = () => {
    if(applied) {
      return <Tag iconSrc={CheckedIcon} iconClass='stroke-deep-green' outlined solid>{t('jobs.have.applied')}</Tag>
    }
    if(engaged) {
      return <Tag iconSrc={SparksIcon} iconClass='stroke-deep-green' outlined solid>{t('jobs.have.engaged')}</Tag>
    }
    if(isInternal) {
      return <Link to='/internal-offers-faqs' onClick={(e) => e.stopPropagation()} target='_blank' rel='noopener noreferrer'>
        <div className='flex items-center gap-x-2'>
          <Tag iconSrc={BloomIcon} iconClass='fill-deep-green' solid>{t('jobs.internal')}</Tag>
          <InfoIcon className='h-6 stroke-gray-600' />
        </div>
      </Link>
    }
    return null
  }

  return (
    <div className={`relative sm:flex flex-row h-full w-full text-sm sm:text-base text-black p-4 ${isOpened ? 'pb-1 sm:pb-3' : ''} `}>
      <div className='flex justify-between items-center mb-2 sm:mb-0 sm:block'>
        <div className='flex h-full items-center gap-x-2 sm:mr-4'>
          <img src={company.logoSrc} alt={company.name} className='sm:w-36 w-32 max-h-14 sm:max-h-16 object-contain object-left sm:object-center' />
        </div>
        <div className='inline-block sm:hidden text-end'>
          {renderOfferStatus()}
          {renderDaysAgo()}
        </div>
      </div>
      <div className='w-full flex flex-col gap-y-2 sm:block'>
        <div className='flex justify-between items-center'>
          <div className='truncate text-ellipsis text-wrap line-clamp-1 sm:text-sm text-xs'>
            <strong>{title} {renderInternshipTag()}{renderRecruitingEventTag()}</strong>
          </div>
          <div className='hidden sm:block'>{renderOfferStatus()}</div>
        </div>
        <div>
          <span className='text-xs font-light'>{company.name}</span>
          {city && <span className='text-xs font-light'> | {city}</span>}
          {workTypes && <span className='text-xs font-light'> | {workTypes}</span>}
          {workShift && <span className='hidden sm:inline-block text-xs font-light'> | {workShift}</span>}
        </div>
        {workShift && <div className='block sm:hidden'><Tag border outlined>{workShift}</Tag></div>}
        <div className='flex justify-between items-center'>
          <div className='flex flex-wrap gap-x-5 items-end'>
            {salary && <Tag transparent iconSrc={EuroIcon}>{salary}</Tag>}
            {experience && <Tag transparent iconSrc={BriefcaseIcon}>{experience}</Tag>}
            {isInternship && isCurricular && <Tag transparent iconSrc={BriefcaseIcon}>{t('jobs.internship.curriculars')}</Tag>}
            {startDateInfo && <Tag transparent iconSrc={CalendarIcon} iconClass='stroke-deep-green'>{startDateInfo}</Tag>}
          </div>
          <div className='hidden sm:block self-end'>{renderDaysAgo()}</div>
          <div className='sm:hidden self-end'>{isInternship && <span className='ml-4 sm:inline-block'>{renderInternshipTag()}</span>}</div>
        </div>
      </div>
    </div>
  )
}

export default JobOfferCard
